<template>
  <div>
    <!-- section pour visualisation une Dynamique colonne-->
    <span
      v-if="info && showBtnTableDynamicColumn(infoValues)"
      class="showBtnTableDynamicColumn"
    >
      <b-button
        icon-left="eye"
        rounded
        size="is-small"
        type="is-dark"
        tabindex="0"
        @keyup.enter="showModal()"
        @keyup.space="showModal()"
        @click="showModal()"
      >
        <LoadingAnimate
          v-if="isLoading"
          :size="'is-small'"
        />
      </b-button>
    </span>
    <!-- modal de multiplicity et dynamique colonne -->
    <b-modal
      v-model="isCardModalActive"
      class="referenceDetails"
    >
      <div class="card">
        <div class="card-header">
          <div
            class="title card-header-title"
            :aria-label="columnTitle"
          >
            <p
              class="columnTitleLabel"
              field="name"
            >
              {{ columnTitle }}
            </p>
          </div>
        </div>
        <div class="card-content">
          <div
            v-for="key in modalArrayObj"
            :key="key.id"
            class="columns modalArrayObj"
          >
            <DatasLink
              v-if="key.columnName !== null"
              :application="application"
              :column-id="key.column"
              :display-value="key.columnName"
              :loaded-references-by-key="{}"
              :reference-type="referenceType"
              :value="key.column"
            />
            <DatasManyLink
              v-else-if="Array.isArray(key.column)"
              :application="application"
              :column-id="key.columnName"
              :info-values="key.column"
              :loaded-references-by-key="{}"
              :multiplicity="true"
              :reference-type="referenceType"
            />
            <div v-else>
              <p
                class="column"
                :aria-label="key.column"
              >
                {{ key.column }} {{ $t("ponctuation.colon") }}
              </p>
            </div>

            <p
              v-if="key.value"
              class="column"
              :aria-label="key.value"
            >
              {{ key.value }}
            </p>
          </div>
        </div>
      </div>
    </b-modal>
  </div>
</template>

<script setup lang="ts">
import LoadingAnimate from "@/components/common/LoadingAnimate.vue";
import services from "@/composable/services";
import useObject from "@/composable/components/object";
import { computed, inject, defineAsyncComponent } from "vue";
import useBoolean from "@/composable/components/boolean";
import { BButton, BModal } from "buefy";
import "@/style/components/DatasDynamicLink.scss";

// Props
interface Props {
  application: any;
  referenceType: string | null | undefined;
  value?: string;
  infoValues: any;
  referenceValue?: any;
  loadedReferencesByKey?: Record<string, any>;
  info?: boolean;
  columnId?: string;
}
const props = defineProps<Props>();

// Emits
const emit = defineEmits<{
  (e: "changedRefValues", payload: any): void;
}>();

// Components
const DatasLink = defineAsyncComponent(() => import("@/components/datas/DatasLink.vue"));
const DatasManyLink = defineAsyncComponent(() => import("@/components/datas/DatasManyLink.vue"));

// Injects
const dataLoader = inject("reference:dataLoader") as any;

// Reactive objects
const isCardModalActive = useBoolean().refBoolean;
const isLoading = useBoolean().refBoolean;
const { reactiveObject: modalArrayObj } = useObject();

// Computed
const applicationName = computed(() => props.application.name);

const columnTitle = computed(() => {
  let displayRef = services.internationalisationService.localeReferenceColumnsNamesForReference(
    props.referenceType,
    props.application,
  );
  if (
    props.application.internationalization?.data[props.referenceType]?.i18n[
      window.localStorage.lang
    ]
  ) {
    displayRef
      = props.application.internationalization.data[props.referenceType].i18n[
        window.localStorage.lang
      ];
  }
  return displayRef;
});

// Methods
function showBtnTableDynamicColumn(tableDynamicColumn: Record<string, any>) {
  let showModal = Object.entries(tableDynamicColumn)
    .filter(a => a[1])
    .map(a => ({ [a[0]]: a[1] }));
  return showModal.length !== 0;
}

async function showModal() {
  isLoading.value = true;
  let columnName: string;

  let modalRefArrayObj = Object.entries(props.infoValues)
    .filter(a => a[1])
    .map(a => ({ [a[0]]: a[1] }));

  const reference = await services.dataService.getData(
    applicationName.value,
    props.referenceType,
  );

  for (let i = 0; i < reference.rows.length; i++) {
    columnName = dataLoader.getValueDisplay(
      reference.rows[i],
      props.application.configuration.applicationDescription.defaultLanguage,
    );
    for (let j = 0; j < modalRefArrayObj.length; j++) {
      if (Object.keys(modalRefArrayObj[j])[0] === reference.rows[i].naturalKey) {
        let column = Object.keys(modalRefArrayObj[j])[0];
        let value = Object.values(modalRefArrayObj[j])[0];
        modalRefArrayObj[j] = {
          ...modalRefArrayObj[j],
          column: column,
          value: value,
          columnName: columnName,
        };
        modalArrayObj[j] = modalRefArrayObj[j];
      }
    }
  }

  emit("changedRefValues", {
    key: props.value,
    refType: props.referenceType,
    modalArrayObj: modalArrayObj.value,
  });

  isCardModalActive.value = true;
  isLoading.value = false;
  return modalArrayObj.value;
}
</script>
